import React from 'react';
import styled from 'styled-components';

import { _ } from '@splunk/ui-utils/i18n';
import Paragraph from '@splunk/react-ui/Paragraph';
import Text, {
    type TextBlurHandler,
    type TextChangeHandler,
} from '@splunk/react-ui/Text';
import { variables } from '@splunk/themes';
import type { Mode } from '@splunk/dashboard-types';

import Tooltip from '@splunk/react-ui/Tooltip';
import { useTextInput } from '../hooks/useTextInput';

const StyledParagraph = styled(Paragraph)`
    min-height: 22px;
    max-width: 100%;
    margin: 0;
    padding-block: ${variables.spacingXSmall} 0;
    padding-inline: ${variables.spacingSmall} ${variables.spacingMedium};
    box-sizing: inherit;
    text-overflow: ellipsis;
`;

const StyledDescriptionInput = styled(Text)`
    flex-grow: 1;
    width: 100%;
    border: none;
`;

const tooltipStyle = { flexGrow: 1, width: '100%' };
const tooltipPointTo = { x: 0, y: 0 };
const descriptionInputStyle = { flexGrow: 1, width: '100%' };

export interface DescriptionProps {
    showInViewMode: boolean;
    mode: Mode;
    onValueChange: (value?: string) => void;
    value?: string;
}

export const Description = React.forwardRef<HTMLElement, DescriptionProps>(
    (
        { showInViewMode, mode, onValueChange, value = '' },
        ref
    ): JSX.Element | null => {
        const {
            onHandleBlur,
            onHandleChange,
            onHandleKeyDown,
            value: inputValue,
        } = useTextInput({
            initialValue: value,
            handleCommit: onValueChange,
            uniqKey: value || '',
        });

        if (mode === 'view' && (!showInViewMode || !inputValue)) {
            return null;
        }

        if (mode === 'edit') {
            return (
                <Tooltip
                    content={_('Dashboard description')}
                    inline={false}
                    style={tooltipStyle}
                    pointTo={tooltipPointTo}
                >
                    <StyledDescriptionInput
                        elementRef={ref as unknown as React.Ref<HTMLDivElement>}
                        data-test="dashboard-description-input"
                        onBlur={onHandleBlur as TextBlurHandler}
                        onChange={onHandleChange as TextChangeHandler}
                        onKeyDown={onHandleKeyDown}
                        placeholder={_('Enter dashboard description.')}
                        style={descriptionInputStyle}
                        value={inputValue}
                        aria-label={_('Dashboard description')}
                    />
                </Tooltip>
            );
        }

        return (
            <StyledParagraph
                elementRef={ref as unknown as React.Ref<HTMLParagraphElement>}
                data-test="dashboard-description"
            >
                {inputValue}
            </StyledParagraph>
        );
    }
);
